/** Physical WASD state belongs to the document, not whichever UI button currently
 * has focus. The native engine still owns normalization, combat gating and ticks.
 * Never synthesize key events on a round transition or retain spell presses.
 */
export class HeldMovement {
  private keys = 0;
  private active = false;
  constructor(
    private readonly publish: (keys: number) => void,
    private readonly doc: Document = document,
    private readonly win: Window = window,
  ) {
    doc.addEventListener("keydown", this.down, true);
    doc.addEventListener("keyup", this.up, true);
    doc.addEventListener("focusin", this.focus, true);
    doc.addEventListener("visibilitychange", this.visibility);
    win.addEventListener("blur", this.clear);
  }
  setActive(active: boolean): void {
    this.active = active;
    if (!active) this.clear();
  }
  clear = (): void => { this.setKeys(0); };
  dispose(): void {
    this.clear();
    this.doc.removeEventListener("keydown", this.down, true);
    this.doc.removeEventListener("keyup", this.up, true);
    this.doc.removeEventListener("focusin", this.focus, true);
    this.doc.removeEventListener("visibilitychange", this.visibility);
    this.win.removeEventListener("blur", this.clear);
  }
  private setKeys(keys: number): void {
    if (keys === this.keys) return;
    this.keys = keys;
    this.publish(keys);
  }
  private bit(code: string): number {
    return ({ KeyW: 1, KeyA: 2, KeyS: 4, KeyD: 8 } as Record<string, number>)[code] ?? 0;
  }
  private editable(target: EventTarget | null): boolean {
    return target instanceof Element && !!target.closest(
      'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"]',
    );
  }
  private down = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey) {
      this.clear();
      return;
    }
    // Repeats after blur/menu clear must not resurrect a stale held key.
    if (!this.active || event.repeat ||
        this.doc.hidden || !this.doc.hasFocus() || this.editable(event.target)) return;
    this.setKeys(this.keys | this.bit(event.code));
  };
  private up = (event: KeyboardEvent): void => {
    // Releases count even on a newly focused menu/input or during countdown.
    this.setKeys(this.keys & ~this.bit(event.code));
  };
  private focus = (event: FocusEvent): void => {
    if (this.editable(event.target)) this.clear();
  };
  private visibility = (): void => {
    if (this.doc.hidden) this.clear();
  };
}
